//LIBRARIES
import React, { Component } from 'react';
import { browserHistory } from 'react-router';
import MediaQuery from 'react-responsive';
import { connect } from 'react-redux';
import { isSubmitting, submit, destroy } from 'redux-form';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import { MuiThemeProvider } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import { createMuiTheme } from '@material-ui/core/styles';

//HELPERS
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import { AccentColor } from 'helpers/AccentColorHelper';
import { drawerStyle } from 'helpers/tableHelpers';
import { toFriendlyDateTimeString, toFriendlyDateString } from 'helpers/Formatter';
import { featureEnabled } from 'helpers/ToggleBot';

//ACTIONS
import * as landlordApplicationsActions from 'actions/landlordApplicationsActions';
import { openDocument } from 'actions/landlordAPIDashboardActions';
import * as landlordApplications from 'actions/landlordApplicationsActions';
import { openEditLease } from 'actions/LeaseDetailsActions';
import { getCompanyDetails } from 'actions/companyDetailsActions';
import { getLandlordTrafficSources } from 'actions/landlordLeadsActions';
import * as landlordProperties from 'actions/landlordPropertiesActions';
import { getCompanyPreferences } from 'actions/companyDetailsActions';
import { deleteApplicant } from 'actions/landlordApplicationsActions';
import { getApplication, syncApplicationWithYardi } from 'actions/landlordApplicationsActions';
import { getSignableDocClientId } from 'actions/landlordSignableDocActions';

//COMPONENTS
import InviteLeadDialog from './components/InviteLeadDialog';
import { ApplicationStatus } from 'components/shared/StatusHelper';
import ApplicationOverview from './components/ApplicationOverview/ApplicationsOverview';
import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import TableFilters from 'components/ui/TableFilters';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';

const statuses = [
  'pending',
  'pending_approval',
  'pending_payment',
  'payment_received',
  'pending_landlord_signature',
  'pending_tenant_signature',
  'pending_unit_assignment',
  'create_lease',
  'pending_all_tenants',
  'flagged',
  'leased_to_other_applicant',
  'unit_reserved_for_other',
  'cancelled',
  'application_denied',
  'pending_lease_approval',
];

export default withMobileDialog()(
  connect(
    (store) => ({
      currentUser: store.userTypes,
      applications: store.landlordApplications.applications,
      application: store.landlordApplications.application,
      fetchingApplications: store.landlordApplications.fetchingApplications,
      applicationsCounts: store.landlordApplications.applicationsCounts,
      leaseDetails: store.leaseDetails.leaseDetails,
      properties: store.landlordProperties.properties,
      submitting: isSubmitting('add_ticket_form')(store),
      disableLeaseWizardSelections:
        store.companyDetails.companyPreferences.disable_lease_wizard_selections &&
        !store.userTypes.admin,
    }),
    {
      ...landlordApplicationsActions,
      openDocument,
      ...landlordProperties,
      ...landlordApplications,
      openEditLease,
      getCompanyDetails,
      getLandlordTrafficSources,
      destroy,
      submit,
      getCompanyPreferences,
      deleteApplicant,
      getApplication,
      getSignableDocClientId,
      syncApplicationWithYardi,
    }
  )(
    class LandlordApplications extends Component {
      state = {
        showFilters: true,
        showInviteLead: false,
        showDrawer: false,
        transferApplication: false,
        filtersActive: false,
        filters: {
          search: '',
          properties: Array(this.props.properties.length).fill(false),
          statuses: Array(statuses.length).fill(false),
        },
        currentTab: 'pending_landlord_action',
        sortKey: '',
        sortOrder: '',
        offset: 0,
      };

      setRef = (ref) => (this.table = ref);

      fetchApplicationsAndStats = () => {
        this.props.getApplicationStats();
        this.fetchApplications();
      };

      componentDidMount = () => {
        this.props.destroy('lease_creator_form');
        this.fetchApplicationsAndStats();
        this.props.getCompanyPreferences();
        this.props.getCompanyDetails();
        this.props.getSignableDocClientId();

        const { applicationID } = this.props.routeParams;
        if (applicationID) {
          this.toggleDrawer(applicationID);
        }
      };

      filterProperties = (booleanArrayMask) => {
        return this.props.properties.filter((_, i) => booleanArrayMask[i]);
      };

      filterStatuses = (booleanArrayMask) => {
        return statuses.filter((_, i) => booleanArrayMask[i]);
      };

      handleClearSearch = (setFieldValue) => {
        this.setState(
          {
            filtersActive: false,
            filters: {
              search: '',
              properties: Array(this.props.properties.length).fill(false),
              statuses: Array(statuses.length).fill(false),
            },
            offset: 0,
          },
          () => this.fetchApplications()
        );

        if (this.table) this.table.scrollToTop(0);
        setFieldValue('search', '');
        setFieldValue('properties', Array(this.props.properties.length).fill(false));
        setFieldValue('statuses', Array(statuses.length).fill(false));
      };

      handleFilters = (values) => {
        const isFiltersUntouched =
          values.search === '' &&
          !values.properties.includes(true) &&
          !values.statuses.includes(true);
        const newState = {
          filtersActive: !isFiltersUntouched,
          filters: {
            search: values.search,
            properties: values.properties,
            statuses: values.statuses,
          },
          drawerVisible: false,
          offset: 0,
        };

        if (this.table) this.table.scrollToTop(0);
        this.setState(newState, () => this.fetchApplications());
      };

      renderApplicantNames = (applicantsString) => {
        if (!applicantsString) return null;
        const applicants = applicantsString.split(',');
        const numberOfApplicants = applicants.length;

        if (numberOfApplicants <= 2) {
          return applicants.map((name, index) => (
            <span key={`${name}.${index}`} style={{ display: 'block' }}>
              {name}
            </span>
          ));
        } else {
          // eslint-disable-next-line
          return applicants.slice(0, 3).map((name, index) => {
            if (index === 0) {
              return (
                <span key={`${name}.${index}`} style={{ display: 'block' }}>
                  {name},
                </span>
              );
            }
            if (index === 1) {
              return (
                <span key={`${name}.${index}`} style={{ display: 'block' }}>
                  {name} and {numberOfApplicants - 2} others
                </span>
              );
            }
          });
        }
      };

      columns = [
        {
          title: 'Status',
          dataKey: 'status',
          sortable: false,
          width: 200,
          render: ({ data }) => (
            <ApplicationStatus status={data} className="ticket_status_label_rent_roll" />
          ),
        },
        {
          title: 'Full Name',
          dataKey: 'cotenant_full_names',
          sortable: false,
          width: 300,
          render: ({ data }) => this.renderApplicantNames(data),
        },
        {
          title: 'Unit',
          dataKey: 'unit_number',
          sortable: true,
          width: 150,
        },
        {
          title: 'Move-in Date',
          dataKey: 'move_in_date',
          sortable: true,
          width: 175,
          render: ({ data }) => toFriendlyDateString(data),
        },
        {
          title: 'Assigned Agent',
          dataKey: 'agent_name',
          sortable: true,
          width: 150,
        },
        {
          title: 'Created At',
          dataKey: 'created_at',
          sortable: true,
          width: 200,
          render: ({ data }) => toFriendlyDateTimeString(data),
        },
        {
          title: 'Applied On',
          dataKey: 'application_completed_at',
          sortable: true,
          width: 200,
          render: ({ data }) => toFriendlyDateTimeString(data),
        },
        {
          title: 'Last Reminded',
          dataKey: 'last_reminded',
          sortable: true,
          width: 250,
          render: ({ data }) => toFriendlyDateTimeString(data),
        },
        {
          title: 'Desired Unit Type',
          dataKey: 'desired_unit_type',
          sortable: true,
          width: 250,
        },
      ];

      handleTabChange = (tabName, _queryObj) => {
        this.setState(
          {
            currentTab: tabName,
            offset: 0,
          },
          () => {
            this.fetchApplications();
          }
        );
        if (this.table) this.table.scrollToTop(0);
      };

      handleSort = (key, order) => {
        this.setState(
          {
            sortKey: key,
            sortOrder: order,
            offset: 0,
          },
          () => {
            this.fetchApplications();
          }
        );
        if (this.table) this.table.scrollToTop(0);
      };

      fetchApplications = () => {
        const { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

        let fetchPromise = null;

        let filterOptions = {
          sort_key: sortKey,
          sort_order: sortOrder,
        };

        if (filtersActive) {
          const propertiesToFilter = this.filterProperties(filters.properties).map(
            (data) => data.value
          );
          const statusesToFilter = this.filterStatuses(filters.statuses);

          filterOptions = {
            query: filters.search,
            property_ids: propertiesToFilter,
            statuses: statusesToFilter,
            sort_key: sortKey,
            sort_order: sortOrder,
          };

          fetchPromise = this.props.getFilteredApplications('all', offset, filterOptions);
        } else {
          fetchPromise = this.props.getFilteredApplications(currentTab, offset, filterOptions);
        }

        fetchPromise.then(() => {
          this.setState({
            offset: this.props.applications.length,
          });
        });
      };

      accentColor = AccentColor();

      filterOptions = [
        {
          useSearch: true,
          options: this.props.properties.map((data) => data.label),
          name: 'properties',
          title: 'Select properties to filter by',
        },
      ];

      onInviteLeadClick = () => {
        if (featureEnabled('application-management')) {
          browserHistory.push('/companies/applications/create_new_application');
        } else {
          this.toggleInviteLead();
        }
      };

      toggleInviteLead = () => {
        this.setState((prevState) => ({
          showInviteLead: !prevState.showInviteLead,
          transferApplication: false,
        }));
      };

      transferApplication = () => {
        this.setState((prevState) => ({
          transferApplication: !prevState.transferApplication,
          showInviteLead: true,
        }));
      };

      getLeasesAndMarketRent = (unitId) => {
        return this.props.getCurrentLeasesOfUnit(unitId).then((response) => {
          return this.props.getUnit(unitId);
        });
      };

      inviteLead = async (formData) => {
        let application_id = this.props.application.id;
        if (!this.state.transferApplication) {
          application_id = null;
        }

        await this.props.inviteLead(formData, application_id);
        this.toggleInviteLead();
        this.getApplicationsAndClearOffset();
      };

      syncApplicationWithYardi = async (applicationID) => {
        await this.props.syncApplicationWithYardi(applicationID);
        await this.props.getApplication(applicationID);
        const leaseID = this.props.application.lease_id;
        if (leaseID) {
          await this.props.openEditLease(leaseID);
        }
      };

      cancelApplication = async (applicationID) => {
        await this.props.cancelApplication(applicationID);
        await this.props.getApplication(applicationID);
        const leaseID = this.props.application.lease_id;
        if (leaseID) {
          await this.props.openEditLease(leaseID);
        }
      };

      toggleDrawer = async (applicationID) => {
        if (!this.state.showDrawer && applicationID && !isNaN(applicationID)) {
          browserHistory.push(`/companies/applications/${applicationID}`);
          if (featureEnabled('application-management')) return;

          await this.props.getApplication(applicationID);
          const leaseID = this.props.application.lease_id;
          if (leaseID) {
            await this.props.openEditLease(leaseID);
          }
          this.setState({ showDrawer: true });
          return;
        }
        browserHistory.push('/companies/applications');
        this.setState({
          showDrawer: false,
        });
      };

      openLeaseCreator = () => {
        this.props.saveApplicationId(this.props.application.id);
        this.props.getLease(this.props.application.lease_id).then(() => {
          browserHistory.push('/companies/review_applicants');
        });
      };

      getApplicationsAndClearOffset = () => {
        this.setState(
          {
            offset: 0,
          },
          () => {
            this.fetchApplicationsAndStats();
          }
        );
      };

      render() {
        const { filters } = this.state;
        const { applications, applicationsCounts } = this.props;
        const disableInviteLeadButton =
          featureEnabled('application-management') &&
          this.props.currentUser?.canReviewApplication === false;

        const bypassGuarantorTab = {
          title: 'Guarantor Signature Bypassed',
          label: 'Guarantor Signature Bypassed',
          value: 'guarantor_signature_bypassed',
          count: applicationsCounts['guarantor_signature_bypassed'] || 0,
          onClick: () =>
            this.handleTabChange('guarantor_signature_bypassed', {
              subset: 'guarantor_signature_bypassed',
            }),
        };

        const filterOptions = [
          {
            useSearch: true,
            options: this.props.properties.map((data) => data.label),
            name: 'properties',
            title: 'Select properties to filter by',
          },
          {
            useSearch: false,
            options: statuses,
            name: 'statuses',
            title: 'Filter by application status',
          },
        ];

        const tabs = [
          {
            title: 'Pending Landlord Action',
            label: 'Pending Landlord Action',
            value: 'pending_landlord_action',
            count: applicationsCounts['pending_landlord_action'] || 0,
            onClick: () =>
              this.handleTabChange('pending_landlord_action', {
                subset: 'pending_landlord_action',
              }),
          },
          {
            title: 'Flagged',
            label: 'Flagged',
            value: 'flagged',
            count: applicationsCounts['flagged'] || 0,
            onClick: () =>
              this.handleTabChange('flagged', {
                subset: 'flagged',
              }),
          },
          {
            title: 'Pending Tenant Action',
            label: 'Pending Tenant Action',
            value: 'pending_tenant_action',
            count: applicationsCounts['pending_tenant_action'] || 0,
            onClick: () =>
              this.handleTabChange('pending_tenant_action', { subset: 'pending_tenant_action' }),
          },
          {
            title: 'Completed',
            label: 'Completed',
            value: 'complete',
            count: applicationsCounts['complete'] || 0,
            onClick: () => this.handleTabChange('complete', { subset: 'complete' }),
          },
          {
            title: 'Denied/Cancelled',
            label: 'Denied/Cancelled',
            value: 'denied_or_cancelled',
            count: applicationsCounts['denied_or_cancelled'] || 0,
            onClick: () =>
              this.handleTabChange('denied_or_cancelled', { subset: 'denied_or_cancelled' }),
          },
          {
            title: 'All',
            label: 'All',
            value: 'all',
            count: applicationsCounts['all'] || 0,
            onClick: () => this.handleTabChange('all', { subset: 'all' }),
          },
        ];

        if (featureEnabled('applications.guarantor_bypass_signature')) {
          tabs.splice(4, 0, bypassGuarantorTab);
        }

        return (
          <StyledMain>
            <StyledHeaderContainer>
              <StyledHeader>
                <HeaderWithPeriod headerText={<span>Applications</span>} newStyle />
                <StyledAddButton
                  accentColor={this.accentColor}
                  onClick={this.onInviteLeadClick}
                  disabled={disableInviteLeadButton}
                >
                  Invite Lead
                </StyledAddButton>
              </StyledHeader>

              <TableFilters
                filters={filters}
                handleSubmitFilters={this.handleFilters}
                handleClearFilters={this.handleClearSearch}
                filterOptions={filterOptions}
                accentColor={this.accentColor}
              />
            </StyledHeaderContainer>

            <StyledTableContainer>
              {this._renderTableView({ applications, tabs })}
              {this._renderMobileView({
                applications,
                tabs,
                currentTab: this.state.currentTab,
                tabChange: this.handleTabChange,
              })}
            </StyledTableContainer>

            <InviteLeadDialog
              showInviteLeadDialog={this.state.showInviteLead}
              toggleInviteLeadDialog={this.toggleInviteLead}
              transferApplication={this.state.transferApplication}
              onlyYardiBasedProperties={
                this.props.application && this.props.application.yardi_based_property
              }
              submitForm={this.inviteLead}
              disableLeaseWizardSelections={this.props.disableLeaseWizardSelections}
            />

            {this.renderApplicationDetails()}
          </StyledMain>
        );
      }

      _renderTableView = ({ applications, tabs }) => {
        let groupBy = 'property_name';
        if (featureEnabled('ui.properties.namespace_by_city')) {
          groupBy = 'property_name_city';
        }
        return (
          <MediaQuery query="(min-device-width: 750px)">
            <TabbedTable
              setRef={this.setRef}
              groupRowsBy={groupBy}
              tabs={tabs}
              columns={this.columns}
              data={applications}
              loadMoreData={this.fetchApplications}
              isLoading={this.props.fetchingApplications}
              filtersActive={this.state.filtersActive}
              onRowClick={(data) => {
                this.toggleDrawer(data.original_id);
              }}
              onColumnClick={(data) => {
                this.handleSort(data.key, data.order);
              }}
            />
          </MediaQuery>
        );
      };

      _renderMobileView = ({ applications, tabs, currentTab, tabChange }) => {
        let groupBy = 'property_name';
        if (featureEnabled('ui.properties.namespace_by_city')) {
          groupBy = 'property_name_city';
        }
        return (
          <MediaQuery query="(max-device-width: 750px)">
            <MobileTable
              groupRowsBy={groupBy}
              data={applications}
              tabs={tabs}
              tabIndex={currentTab}
              tabChange={tabChange}
              filtersActive={this.state.filtersActive}
              columns={this.columns}
              title="cotenant_full_names"
              tag="status"
              stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
              fieldsAboveFold={[
                {
                  title: 'Assigned Agent',
                  key: 'agent_name',
                },
                {
                  title: 'Applied To',
                  key: 'applied_on',
                },
              ]}
              fieldsBelowFold={[
                {
                  title: 'Unit Number',
                  key: 'unit_number',
                },
                {
                  title: 'Move In Date',
                  key: 'move_in_date',
                },
                {
                  title: 'Last Reminded',
                  key: 'last_reminded',
                },
              ]}
              onRowClick={(data) => {
                this.toggleDrawer(data.id);
              }}
            />
          </MediaQuery>
        );
      };

      renderApplicationDetails = () => (
        <MuiThemeProvider theme={createMuiTheme(drawerStyle)}>
          <Drawer open={this.state.showDrawer} onClose={this.toggleDrawer} anchor="left">
            <ApplicationOverview
              toggleDrawer={this.toggleDrawer}
              leaseDetails={this.props.leaseDetails}
              getApplicationsAndClearOffset={this.getApplicationsAndClearOffset}
              openLeaseCreator={this.openLeaseCreator}
              toggleApplication={this.transferApplication}
              deleteApplicant={this.props.deleteApplicant}
              getApplication={this.props.getApplication}
              syncApplicationWithYardi={this.syncApplicationWithYardi}
              cancelApplication={this.cancelApplication}
            />
          </Drawer>
        </MuiThemeProvider>
      );
    }
  )
);
